import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AtSign, Bold, CaseSensitive, ChevronDown, Code, Italic, Link2, List, ListOrdered, Mic, Plus, SendHorizontal, Smile, SquareCode, Strikethrough, TextQuote, Underline, Video, SquareSlash } from "lucide-react";
import { toast } from "sonner";
import { EmojiPicker } from "./EmojiPicker";
import { Avatar } from "./Avatar";
import { AttachmentTray } from "./Attachments";
import { useChat } from "@/lib/chat";
import { analytics } from "@/lib/analytics";
import { useComposerTelemetry } from "@/lib/telemetry";
import { errMsg } from "@/lib/api";
import { MAX_FILE_MB, uploadFile } from "@/lib/upload";

const SPECIAL = [
  { handle: "channel", display_name: "channel", desc: "Notify everyone in this channel" },
  { handle: "here", display_name: "here", desc: "Notify every active member in this channel" },
  { handle: "everyone", display_name: "everyone", desc: "Notify everyone in the workspace" },
];
const MAX_FILES = 10;

export const Composer = ({ conversationId, parentId = null, placeholder, initialText = "", onSend, autoFocus, inPanel, editing, onCancel, compact }) => {
  const { users, sendTyping, saveDraft, typing, current, meId, can } = useChat();
  const [text, setText] = useState(initialText);
  const [showFmt, setShowFmt] = useState(!compact);
  const [sending, setSending] = useState(false);
  const [ac, setAc] = useState(null);
  const [acIndex, setAcIndex] = useState(0);
  const [uploads, setUploads] = useState([]);
  const uploadsRef = useRef([]);
  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);
  const ref = useRef(null);
  const fileInput = useRef(null);
  const draftTimer = useRef(null);
  const textRef = useRef(text);
  const promises = useRef({});
  textRef.current = text;
  const tel = useComposerTelemetry({ conversationId, parentId, editing });
  const telRef = useRef(tel);
  telRef.current = tel;

  const initialRef = useRef(initialText);
  initialRef.current = initialText;
  useEffect(() => { setText(initialRef.current); setUploads([]); }, [conversationId, parentId]);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus, conversationId, parentId]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.5)}px`;
  }, [text]);

  useEffect(() => () => {
    clearTimeout(draftTimer.current);
    telRef.current.onAbandon(textRef.current, "left");
    if (!editing && conversationId) saveDraft(conversationId, textRef.current, parentId);
  }, [conversationId, parentId, editing, saveDraft]);

  const candidates = useMemo(() => {
    if (!ac) return [];
    const q = ac.query.toLowerCase();
    const specials = parentId ? [] : SPECIAL.filter((s) => s.handle.startsWith(q));
    const people = users.filter((u) => !u.is_system_bot && u.is_active !== false && (u.display_name.toLowerCase().includes(q) || u.handle.toLowerCase().includes(q))).slice(0, 8);
    return [...specials.map((s) => ({ ...s, special: true })), ...people];
  }, [ac, users, parentId]);

  const updateAc = (val, caret) => {
    const before = val.slice(0, caret);
    const m = before.match(/(^|\s)@([\w.-]*)$/);
    if (m) { setAc({ query: m[2], start: caret - m[2].length - 1 }); setAcIndex(0); } else setAc(null);
  };

  const onChange = (e) => {
    const val = e.target.value;
    tel.onInput(text, val);
    setText(val);
    updateAc(val, e.target.selectionStart);
    if (!editing && conversationId) {
      sendTyping(conversationId, parentId);
      clearTimeout(draftTimer.current);
      draftTimer.current = setTimeout(() => saveDraft(conversationId, val, parentId), 900);
    }
  };

  const insertAt = (start, end, snippet, caretOffset = snippet.length) => {
    const next = text.slice(0, start) + snippet + text.slice(end);
    setText(next);
    requestAnimationFrame(() => { ref.current?.focus(); ref.current?.setSelectionRange(start + caretOffset, start + caretOffset); });
    return next;
  };

  const pickCandidate = (c) => {
    if (!ac) return;
    const caret = ref.current?.selectionStart ?? text.length;
    insertAt(ac.start, caret, `@${c.handle} `);
    setAc(null);
    analytics.track("mention_inserted", { category: "composer", label: c.special ? `@${c.handle}` : c.handle, target_id: c.id, conversation_id: conversationId });
  };

  const wrap = (marker, label) => {
    const el = ref.current;
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = text.slice(s, e);
    insertAt(s, e, `${marker}${sel}${marker}`, sel ? marker.length * 2 + sel.length : marker.length);
    analytics.track("format_applied", { category: "composer", label, conversation_id: conversationId });
  };
  const prefixLines = (prefix, label, numbered) => {
    const el = ref.current;
    const s = el.selectionStart, e = el.selectionEnd;
    const lineStart = text.lastIndexOf("\n", s - 1) + 1;
    const lineEnd = text.indexOf("\n", e) === -1 ? text.length : text.indexOf("\n", e);
    const lines = text.slice(lineStart, lineEnd).split("\n").map((l, i) => (numbered ? `${i + 1}. ${l}` : `${prefix}${l}`));
    insertAt(lineStart, lineEnd, lines.join("\n"));
    analytics.track("format_applied", { category: "composer", label, conversation_id: conversationId });
  };
  const codeBlock = () => {
    const el = ref.current;
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = text.slice(s, e);
    insertAt(s, e, `\`\`\`\n${sel}\n\`\`\``, 4 + sel.length);
    analytics.track("format_applied", { category: "composer", label: "code_block", conversation_id: conversationId });
  };
  const link = () => {
    const el = ref.current;
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = text.slice(s, e);
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    insertAt(s, e, sel ? `<${url}|${sel}>` : url);
    analytics.track("format_applied", { category: "composer", label: "link", conversation_id: conversationId });
  };
  const insertEmoji = (name, ch) => {
    const caret = ref.current?.selectionStart ?? text.length;
    insertAt(caret, caret, ch);
    analytics.track("emoji_inserted", { category: "composer", label: name, conversation_id: conversationId });
  };
  const triggerMention = () => {
    const caret = ref.current?.selectionStart ?? text.length;
    const needsSpace = caret > 0 && !/\s/.test(text[caret - 1]);
    const next = insertAt(caret, caret, `${needsSpace ? " " : ""}@`);
    updateAc(next, caret + (needsSpace ? 2 : 1));
  };

  // ---- attachments ----
  const patchUpload = (key, patch) => setUploads((us) => us.map((u) => (u.key === key ? { ...u, ...patch } : u)));
  const addFiles = useCallback((fileList, source) => {
    const files = Array.from(fileList || []);
    if (!files.length || editing) return;
    const room = MAX_FILES - uploadsRef.current.length;
    if (room <= 0) { toast.error(`You can attach up to ${MAX_FILES} files per message`); return; }
    const picked = files.slice(0, room);
    if (picked.length < files.length) toast.warning(`Only the first ${room} files were added (max ${MAX_FILES} per message)`);
    const items = picked.map((file) => ({ key: `${Date.now()}-${Math.random().toString(16).slice(2)}`, file, progress: 0, result: null, error: null, controller: new AbortController() }));
    items.forEach((it) => {
      analytics.track("file_attached", { category: "files", target: "composer", label: source, conversation_id: conversationId, metadata: { name: it.file.name, size: it.file.size, type: it.file.type, thread: !!parentId } });
      if (it.file.size > MAX_FILE_MB * 1024 * 1024) { it.error = `Larger than ${MAX_FILE_MB} MB`; return; }
      promises.current[it.key] = uploadFile(it.file, { conversationId, signal: it.controller.signal, onProgress: (p) => patchUpload(it.key, { progress: p }) })
        .then((result) => { patchUpload(it.key, { result, progress: 1 }); return result; })
        .catch((e) => { if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") patchUpload(it.key, { error: errMsg(e, "Upload failed") }); return null; });
    });
    uploadsRef.current = [...uploadsRef.current, ...items];
    setUploads((prev) => [...prev, ...items]);
    requestAnimationFrame(() => ref.current?.focus());
  }, [conversationId, parentId, editing]);
  const removeUpload = (key) => {
    setUploads((us) => { const it = us.find((u) => u.key === key); it?.controller.abort(); return us.filter((u) => u.key !== key); });
    delete promises.current[key];
    analytics.track("file_attachment_removed", { category: "files", target: "composer", conversation_id: conversationId });
  };
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); addFiles(files, "paste"); return; }
    const pasted = e.clipboardData?.getData("text") || "";
    if (pasted) tel.onPaste(pasted.length);
  };

  useEffect(() => {
    if (editing) return undefined;
    const zone = ref.current?.closest(inPanel ? ".tc-panel" : ".tc-main");
    if (!zone) return undefined;
    let depth = 0;
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const label = () => `Upload to ${parentId ? "this thread" : current ? (current.type === "channel" ? `#${current.name}` : current.display_name) : "this conversation"}`;
    const enter = (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth++; zone.classList.add("tc-dropzone"); zone.dataset.dropLabel = label(); };
    const over = (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
    const leave = (e) => { if (!hasFiles(e)) return; depth = Math.max(0, depth - 1); if (!depth) zone.classList.remove("tc-dropzone"); };
    const drop = (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth = 0; zone.classList.remove("tc-dropzone"); addFiles(e.dataTransfer.files, "drop"); };
    zone.addEventListener("dragenter", enter); zone.addEventListener("dragover", over); zone.addEventListener("dragleave", leave); zone.addEventListener("drop", drop);
    return () => { zone.classList.remove("tc-dropzone"); zone.removeEventListener("dragenter", enter); zone.removeEventListener("dragover", over); zone.removeEventListener("dragleave", leave); zone.removeEventListener("drop", drop); };
  }, [addFiles, editing, inPanel, parentId, current]);

  const uploading = uploads.some((u) => !u.result && !u.error);
  const hasErrors = uploads.some((u) => u.error);

  const submit = useCallback(async () => {
    const value = text.trim();
    const attachable = uploads.filter((u) => !u.error);
    if ((!value && !attachable.length) || sending || hasErrors) return;
    setSending(true);
    try {
      const results = await Promise.all(attachable.map((u) => (u.result ? u.result : promises.current[u.key])));
      const fileIds = results.filter(Boolean).map((r) => r.id);
      if (!value && !fileIds.length) return;
      await onSend(value, fileIds);
      telRef.current.onSend(value, fileIds.length);
      setText("");
      setUploads([]);
      promises.current = {};
      clearTimeout(draftTimer.current);
      if (!editing && conversationId) saveDraft(conversationId, "", parentId);
      requestAnimationFrame(() => { if (ref.current) { ref.current.style.height = "auto"; ref.current.focus(); } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  }, [text, uploads, sending, hasErrors, onSend, editing, conversationId, parentId, saveDraft]);

  const onKeyDown = (e) => {
    tel.onKeyDown(e);
    if (ac && candidates.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setAcIndex((i) => (i + 1) % candidates.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setAcIndex((i) => (i - 1 + candidates.length) % candidates.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickCandidate(candidates[acIndex]); return; }
      if (e.key === "Escape") { setAc(null); return; }
    }
    if (e.key === "Escape" && editing) { onCancel?.(); return; }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); return; }
    if (e.metaKey || e.ctrlKey) {
      if (e.key === "b") { e.preventDefault(); wrap("*", "bold"); }
      else if (e.key === "i") { e.preventDefault(); wrap("_", "italic"); }
      else if (e.key === "u") { e.preventDefault(); wrap("__", "underline"); }
      else if (e.key === "X" && e.shiftKey) { e.preventDefault(); wrap("~", "strike"); }
      else if (e.key === "C" && e.shiftKey) { e.preventDefault(); wrap("`", "code"); }
      else if (e.key === "u" && e.shiftKey) { e.preventDefault(); fileInput.current?.click(); }
    }
  };

  const typers = Object.entries(typing[parentId || conversationId] || {}).filter(([uid]) => uid !== meId).map(([, n]) => n);
  const ready = (text.trim().length > 0 || uploads.some((u) => u.result)) && !sending && !hasErrors && !uploading;
  const ph = placeholder || (current ? (current.type === "channel" ? `Message #${current.name}` : `Message ${current.display_name}`) : "Message");

  return (
    <div className={`tc-composer-wrap${inPanel ? " in-panel" : ""}`} data-testid={editing ? "message-edit-composer" : parentId ? "thread-composer" : "message-composer"}>
      {ac && candidates.length > 0 && (
        <div className="tc-autocomplete tc-fade-in" data-testid="mention-autocomplete" style={inPanel ? { left: 16, right: 16 } : undefined}>
          <div className="tc-autocomplete__head">{ac.query ? `People matching "@${ac.query}"` : "People and groups"}</div>
          {candidates.map((c, i) => (
            <button key={c.special ? `s-${c.handle}` : c.id} className={`tc-autocomplete__item${i === acIndex ? " is-active" : ""}`} onMouseDown={(e) => { e.preventDefault(); pickCandidate(c); }} onMouseEnter={() => setAcIndex(i)} data-testid={`mention-option-${c.handle}`}>
              {c.special ? <span className="special">@</span> : <Avatar user={c} size={20} radius={4} />}
              <span>{c.special ? `@${c.handle}` : c.display_name}</span>
              {!c.special && <span className="handle">@{c.handle}</span>}
              {c.special && <span className="desc">{c.desc}</span>}
              {!c.special && c.title && <span className="desc">{c.title}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="tc-composer">
        {showFmt && (
          <div className="tc-composer__toolbar" data-testid="composer-format-toolbar">
            <button className="tc-fmt" title="Bold (⌘B)" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap("*", "bold")} data-testid="format-bold"><Bold /></button>
            <button className="tc-fmt" title="Italic (⌘I)" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap("_", "italic")} data-testid="format-italic"><Italic /></button>
            <button className="tc-fmt" title="Underline (⌘U)" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap("__", "underline")} data-testid="format-underline"><Underline /></button>
            <button className="tc-fmt" title="Strikethrough (⌘⇧X)" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap("~", "strike")} data-testid="format-strike"><Strikethrough /></button>
            <span className="tc-fmt__sep" />
            <button className="tc-fmt" title="Link (⌘⇧U)" onMouseDown={(e) => e.preventDefault()} onClick={link} data-testid="format-link"><Link2 /></button>
            <button className="tc-fmt" title="Ordered list" onMouseDown={(e) => e.preventDefault()} onClick={() => prefixLines("1. ", "ordered_list", true)} data-testid="format-ol"><ListOrdered /></button>
            <button className="tc-fmt" title="Bulleted list" onMouseDown={(e) => e.preventDefault()} onClick={() => prefixLines("- ", "bulleted_list")} data-testid="format-ul"><List /></button>
            <span className="tc-fmt__sep" />
            <button className="tc-fmt" title="Blockquote (⌘⇧9)" onMouseDown={(e) => e.preventDefault()} onClick={() => prefixLines("> ", "quote")} data-testid="format-quote"><TextQuote /></button>
            <button className="tc-fmt" title="Code (⌘⇧C)" onMouseDown={(e) => e.preventDefault()} onClick={() => wrap("`", "code")} data-testid="format-code"><Code /></button>
            <button className="tc-fmt" title="Code block (⌘⌥⇧C)" onMouseDown={(e) => e.preventDefault()} onClick={codeBlock} data-testid="format-codeblock"><SquareCode /></button>
          </div>
        )}
        <textarea ref={ref} className="tc-composer__input" value={text} onChange={onChange} onKeyDown={onKeyDown} onPaste={onPaste} onBlur={tel.onBlur} onClick={(e) => updateAc(text, e.target.selectionStart)} placeholder={ph} rows={1} data-testid="composer-input" />
        {!editing && <AttachmentTray items={uploads} onRemove={removeUpload} />}
        <div className="tc-composer__bottom">
          {!editing && (
            <>
              <input ref={fileInput} type="file" multiple style={{ display: "none" }} onChange={(e) => { addFiles(e.target.files, "picker"); e.target.value = ""; }} data-testid="composer-file-input" />
              <button className="tc-composer__plus" title={can("files.upload") ? `Attach files (up to ${MAX_FILE_MB} MB each)` : "You don't have permission to upload files"} onClick={() => (can("files.upload") ? fileInput.current?.click() : toast.error("You don't have permission to upload files. Ask a workspace admin."))} data-testid="composer-attach-button" aria-disabled={!can("files.upload")}><Plus size={16} /></button>
            </>
          )}
          <button className={`tc-fmt${showFmt ? " is-on" : ""}`} title="Hide formatting" onMouseDown={(e) => e.preventDefault()} onClick={() => setShowFmt((s) => !s)} data-testid="composer-format-toggle"><CaseSensitive /></button>
          <EmojiPicker onPick={insertEmoji} align="start">
            <button className="tc-fmt" title="Emoji" onMouseDown={(e) => e.preventDefault()} data-testid="composer-emoji-button"><Smile /></button>
          </EmojiPicker>
          <button className="tc-fmt" title="Mention someone" onMouseDown={(e) => e.preventDefault()} onClick={triggerMention} data-testid="composer-mention-button"><AtSign /></button>
          {!editing && (
            <>
              <button className="tc-fmt" title="Record video clip" onClick={() => toast("Video clips are coming soon")} data-testid="composer-video-button"><Video /></button>
              <button className="tc-fmt" title="Record audio clip" onClick={() => toast("Audio clips are coming soon")} data-testid="composer-audio-button"><Mic /></button>
              <button className="tc-fmt" title="Shortcuts" onClick={() => toast("Try formatting: *bold* _italic_ ~strike~ `code` > quote")} data-testid="composer-shortcuts-button"><SquareSlash /></button>
            </>
          )}
          {editing ? (
            <div className="tc-inline-edit__actions" style={{ marginLeft: "auto", marginTop: 0 }}>
              <button className="tc-btn tc-btn--outline sm" onClick={onCancel} data-testid="edit-cancel-button">Cancel</button>
              <button className="tc-btn tc-btn--primary sm" onClick={submit} disabled={!ready} data-testid="edit-save-button">Save</button>
            </div>
          ) : (
            <div className={`tc-composer__send${ready ? " is-ready" : ""}`}>
              <button onClick={submit} disabled={!ready} title={uploading ? "Waiting for uploads to finish…" : "Send now (Enter)"} data-testid="composer-send-button"><SendHorizontal /></button>
              <span className="sep" />
              <button className="caret" title="Schedule for later" onClick={() => toast("Scheduled sends are coming soon")} data-testid="composer-send-options"><ChevronDown size={14} /></button>
            </div>
          )}
        </div>
      </div>
      {!editing && (
        <div className="tc-typing" data-testid="typing-indicator">
          {typers.length > 0 && <><b>{typers.slice(0, 2).join(" and ")}</b>{typers.length > 2 ? ` and ${typers.length - 2} others are` : typers.length > 1 ? " are" : " is"} typing…</>}
        </div>
      )}
    </div>
  );
};
